import * as feedApi from '@apis/feeds-v2';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Switch } from 'antd';
import React, { useEffect, useState } from 'react';

type MainFeedV2Config = {
  type: 'main-feed-v2-config';
  data?: Array<{ type: string; showHomeVoteCards?: boolean; tabOrder?: string[]; [key: string]: any }>;
  [key: string]: any;
};

const HOME_TAB = 'all';
const REORDERABLE_TABS = ['vote', 'review', 'news'];
const DEFAULT_TAB_ORDER = [HOME_TAB, ...REORDERABLE_TABS];

const TAB_LABELS: Record<string, string> = {
  all: '홈',
  vote: '투표',
  review: '후기',
  news: '소식',
};

/** 홈(all)은 항상 첫 번째 고정, 나머지 탭만 순서 변경 */
const normalizeTabOrder = (order?: string[]) => {
  const configured = Array.isArray(order) ? order : [];
  const reorderable = [
    ...configured.filter((board) => REORDERABLE_TABS.includes(board)),
    ...REORDERABLE_TABS.filter((board) => !configured.includes(board)),
  ];
  return [HOME_TAB, ...reorderable];
};

const SortableTabItem: React.FC<{ id: string; label: string }> = ({ id, label }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    border: '1px solid #E8EAED',
    borderRadius: '6px',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span style={{ color: '#999', fontSize: '12px' }}>⋮⋮</span>
      <span style={{ fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#999', fontSize: '12px' }}>({id})</span>
    </div>
  );
};

export const LoungeConfigEdit: React.FC = () => {
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const [showHomeVoteCards, setShowHomeVoteCards] = useState(true);
  const [tabOrder, setTabOrder] = useState<string[]>(DEFAULT_TAB_ORDER);
  const [dirty, setDirty] = useState(false);

  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const configQuery = useQuery({
    queryKey: ['main-feed-v2-config'],
    queryFn: () => feedApi.getMainFeedV2Config(),
  });

  useEffect(() => {
    const config = (configQuery.data?.data || {}) as MainFeedV2Config;
    const loungeConfig = (config.data || []).find((d) => d?.type === 'lounge-config');
    setShowHomeVoteCards(loungeConfig?.showHomeVoteCards ?? true);
    setTabOrder(normalizeTabOrder(loungeConfig?.tabOrder));
    setDirty(false);
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (next: { showHomeVoteCards: boolean; tabOrder: string[] }) => {
      const existing = (configQuery.data?.data || {}) as MainFeedV2Config;
      const existingItems = Array.isArray(existing.data) ? existing.data : [];
      const nextItems = [
        ...existingItems.filter((d) => d?.type !== 'lounge-config'),
        {
          type: 'lounge-config',
          showHomeVoteCards: next.showHomeVoteCards,
          tabOrder: normalizeTabOrder(next.tabOrder),
        },
      ];
      const payload: MainFeedV2Config = {
        ...(existing || {}),
        type: 'main-feed-v2-config',
        data: nextItems,
      };
      return await feedApi.updateMainFeedV2Config(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['main-feed-v2-config'] });
      setDirty(false);
      dialog.open({ type: 'ok', title: '알림', content: '라운지 설정을 저장했습니다.' });
    },
    onError: (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const reorderableTabOrder = tabOrder.filter((board) => board !== HOME_TAB);

  const onSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    if (active.id === HOME_TAB || over.id === HOME_TAB) {
      return;
    }
    const oldIndex = reorderableTabOrder.findIndex((board) => board === active.id);
    const newIndex = reorderableTabOrder.findIndex((board) => board === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    setTabOrder([HOME_TAB, ...arrayMove(reorderableTabOrder, oldIndex, newIndex)]);
    setDirty(true);
  };

  const handleSave = () => {
    saveMutation.mutate({ showHomeVoteCards, tabOrder: normalizeTabOrder(tabOrder) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '360px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500 }}>홈 탭 투표 카드</div>
          <div style={{ fontSize: '13px', color: '#666' }}>라운지 홈 탭 상단에 투표 카드 목록을 노출합니다</div>
        </div>
        <Switch
          checked={showHomeVoteCards}
          disabled={saveMutation.isPending || configQuery.isLoading}
          onChange={(checked) => {
            setShowHomeVoteCards(checked);
            setDirty(true);
          }}
        />
      </div>

      <div>
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>탭 순서</div>
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
          홈은 첫 번째로 고정됩니다. 나머지 탭만 드래그하여 순서를 변경합니다
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: '#F5F6F8',
              border: '1px solid #E8EAED',
              borderRadius: '6px',
              color: '#666',
            }}>
            <span style={{ fontWeight: 500 }}>{TAB_LABELS[HOME_TAB]}</span>
            <span style={{ fontSize: '12px' }}>(고정)</span>
          </div>
          <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
            <SortableContext items={reorderableTabOrder} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {reorderableTabOrder.map((board) => (
                  <SortableTabItem key={board} id={board} label={TAB_LABELS[board] || board} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <div>
        <Button type="primary" loading={saveMutation.isPending} disabled={!dirty} onClick={handleSave}>
          라운지 설정 저장
        </Button>
      </div>
    </div>
  );
};

export default LoungeConfigEdit;
